import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { BASE_URL } from '../constants/globalContants';
import auth from '../mobx/auth-state';
import notificationState from '../mobx/notification-state';

// API response structure (what the backend returns in response.data)
interface IApiResponse<D = any> {
    message?: string;
    errors?: string[];
    status?: number;
    data?: D;
}

class ApiBase {
    public readonly instance: AxiosInstance = axios.create({
        baseURL: BASE_URL,
    });

    public constructor() {
        this._initializeRequestInterceptor();
        this._initializeResponseInterceptor();
    }

    private _initializeRequestInterceptor = (): void => {
        this.instance.interceptors.request.use(
            (config) => {
                config.headers = config.headers || {}; // Ensure headers object is initialized
                const authToken = auth.currentUser?.authToken;
                if (authToken && typeof authToken === 'string') {
                    config.headers['Authorization'] = `Bearer ${authToken}`;
                }
                return config;
            },
            (error: AxiosError) => Promise.reject(error)
        );
    };

    private _initializeResponseInterceptor = (): void => {
        this.instance.interceptors.response.use(
            this._handleResponse,
            this._handleError,
        );
    };

    private _handleResponse = <T = any>(response: AxiosResponse<T>): T => {
        return response.data;
    };

    public _handleError = (error: AxiosError<IApiResponse>): Promise<never> => {
        if (error?.response?.status === 401) {
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong, try again...';
            notificationState.addToasterError(errorMessage);
            auth.doLogout();
        }
        return Promise.reject(error);
    };

    public get<T = any, R = IApiResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.instance.get(url, config);
    }

    public post<T = any, R = IApiResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return this.instance.post(url, data, config);
    }

    public put<T = any, R = IApiResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return this.instance.put(url, data, config);
    }

    public delete<T = any, R = IApiResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.instance.delete(url, config);
    }

}
export const apiClient = new ApiBase();
// export default ApiBase;

